import type {
  AllMetricIntervalParams,
  BranchDetailsParams,
  ClinicianDetailsParams,
  DashboardMetricsParams,
  MetricDrillDownParams,
  DisciplineNotificationParams,
  MetricRelationsParams,
  MetricSummaryParams,
} from "@/components/daylight-insight/api/types";
import type {
  CharlieDrillParams,
  CharlieFeedParams,
} from "@/components/charlie/types";

/**
 * Every query key in the app, in one place.
 *
 * ── Shape ──────────────────────────────────────────────────────────────────
 *   ['dl', userKey, domain, resource, ...params]
 *
 *   'dl'      a namespace root, so the whole app's cache can be addressed at
 *             once without touching anything else in the client
 *   userKey   MANDATORY — see `useUserKey`. Keeps one user's cache unreachable
 *             from another's.
 *   domain    'config' | 'prefs' | 'dashboard' | 'drill' | 'allMetrics'.
 *             Enables prefix invalidation of a whole area in one call.
 *   params    the params object ITSELF, not a JSON string. TanStack hashes
 *             object keys deterministically (sorted), so `{a:1,b:2}` and
 *             `{b:2,a:1}` produce the same key — strictly better than the
 *             `JSON.stringify` the hand-rolled hooks used, which was
 *             field-order dependent.
 *
 * ── What must NOT appear in a key ──────────────────────────────────────────
 *   • `requestToServer` or any function — not serializable.
 *   • The auth token — it rotates on every silent refresh, which would wipe the
 *     entire cache roughly hourly.
 *   • Display labels (branch/payor NAMES). Keys use the SystemIDs actually sent
 *     to the API; labels are a UI concern.
 *   • Client-side-only filters (metric selection, search text, aging bucket,
 *     the clinician sliders). They filter rows in the browser and are not sent
 *     to any SP — putting them in a key would fragment the cache and fire
 *     requests for work that never leaves the client.
 */
export const qk = {
  /** Everything this user has cached. `invalidateQueries({ queryKey: qk.all(u) })`
   *  reaches all of it. */
  all: (u: string) => ["dl", u] as const,

  // ── Bootstrap (tier A) ─────────────────────────────────────────────────
  config: (u: string) => [...qk.all(u), "config", "dashboard"] as const,
  /** The metric dictionary, per service line. Bootstrap tier like `config`:
   *  definitions change only by admin action, and Home Health and Hospice
   *  define different metric sets — hence the service line in the key. */
  metricDefinitions: (u: string, serviceLineSystemID: string) =>
    [...qk.all(u), "config", "metricDefinitions", serviceLineSystemID] as const,

  // ── Saved preferences (tier B) ─────────────────────────────────────────
  prefs: (u: string) => [...qk.all(u), "prefs"] as const,
  /** One entry per dashboard scope. All Metrics' own views live under
   *  `dashboardID: null`, which is a DISTINCT entry — saving a dashboard view
   *  must not invalidate them. */
  perfs: (u: string, dashboardID: number | string | null) =>
    [...qk.prefs(u), "customViews", dashboardID ?? null] as const,
  /** The key an IDLE perfs hook parks on (no dashboard id known yet).
   *
   *  It must not be `perfs(u, null)`: that is All Metrics' real scope
   *  (`@DashboardID = NULL`), and a disabled observer sharing a key with an
   *  active query still READS its cached data — which would hand a dashboard
   *  that is merely waiting for its id All Metrics' saved views and flip its
   *  `ready` flag early. */
  perfsIdle: (u: string) => [...qk.prefs(u), "customViews", "__idle__"] as const,

  // ── Dashboard (tier C) ─────────────────────────────────────────────────
  dashboard: (u: string) => [...qk.all(u), "dashboard"] as const,
  metrics: (u: string, p: DashboardMetricsParams | null) =>
    [...qk.dashboard(u), "metrics", p] as const,
  branchDetails: (u: string, p: BranchDetailsParams | null) =>
    [...qk.dashboard(u), "branchDetails", p] as const,
  clinicianDetails: (u: string, p: ClinicianDetailsParams | null) =>
    [...qk.dashboard(u), "clinicianDetails", p] as const,
  disciplineNotifications: (u: string, p: DisciplineNotificationParams | null) =>
    [...qk.dashboard(u), "disciplineNotifications", p] as const,

  // ── Drill-down (tier C) ────────────────────────────────────────────────
  drill: (u: string) => [...qk.all(u), "drill"] as const,
  relations: (u: string, p: MetricRelationsParams | null) =>
    [...qk.drill(u), "relations", p] as const,
  /** `endpoint` is part of the key and MUST stay that way: By Branch, By Payor,
   *  By Referral Source, By Sales Rep and By Discipline all send the SAME params
   *  shape to different SPs. Without it, all five drill tabs would collide on
   *  one cache entry and show each other's numbers. */
  summary: (u: string, endpoint: string, p: MetricSummaryParams | null) =>
    [...qk.drill(u), "summary", endpoint, p] as const,
  branchTrend: (u: string, p: MetricRelationsParams | null) =>
    [...qk.drill(u), "branchTrend", p] as const,

  // ── Charlie (tier E) ───────────────────────────────────────────────────
  charlie: (u: string) => [...qk.all(u), "charlie"] as const,
  /** The prefix a write invalidates: every branch/service-line combination of
   *  the feed, and nothing else. A decision changes which list a task is in —
   *  it does not change the records behind any signal, so the drill entries
   *  below deliberately sit outside this. */
  charlieFeeds: (u: string) => [...qk.charlie(u), "feed"] as const,
  charlieFeed: (u: string, p: CharlieFeedParams) => [...qk.charlieFeeds(u), p] as const,
  /** One entry per task's records — keyed by the task, so re-opening the same
   *  drill inside the window costs nothing. */
  charlieDrill: (u: string, p: CharlieDrillParams) =>
    [...qk.charlie(u), "drillDown", p] as const,

  // ── All Metrics ────────────────────────────────────────────────────────
  allMetrics: (u: string) => [...qk.all(u), "allMetrics"] as const,
  interval: (u: string, p: AllMetricIntervalParams | null) =>
    [...qk.allMetrics(u), "interval", p] as const,
  /** Tier D — record-level, larger payloads, shorter gcTime. */
  records: (u: string, p: MetricDrillDownParams | null) =>
    [...qk.allMetrics(u), "drillDown", p] as const,
};
